import { DefaultSystemKey } from '@/config'
import { create } from 'zustand'
import { menuKeysMap } from './menu'
import { SystemEnum } from '@/config/enum'
import { ThemeType } from '@/types/global';
import { ThemeTypeObj } from '@/config/theme';
import { persist } from 'zustand/middleware';
import { ExportTask } from '@/utils/exportTaskManager';
import { DownloadTask } from '@/utils/downloadTaskManager';
import { UploadTask } from '@/utils/uploadTaskManager';

interface GlobalState {
  menuKeysMap: any;
  userInfo: UserInfoT;
  collapsed: boolean;
  authCodes: any[];
  currentEnterprise: any; // 当前企业
  // 是否经过授权
  authorized: boolean;
  // 当前企业信息
  enterprisesList: any[];
  token: string; // 鉴权token
  tenantId: string; // 租户id，U开头代表个人
  productInfo: any; // 产品信息
  preRoute: string; // 上一个路由
  // 导航菜单
  curHeaderMenuKey: string;
  // 后端记录的最后一次登录的 tenantId
  lastLoginTenantId: string | null;
  unitConvertMap: any; // {标准单位: 换算单位}
  unitAliasMap: any; // {标准单位: [别名1，别名2]}
  // 默认的系统key
  systemKey:
    | SystemEnum.LJXT
    | SystemEnum.SYSTEM
    | SystemEnum.YJGL
    | SystemEnum.ZBGL
    | SystemEnum.ZXZY
    | SystemEnum.ZLWD
    | SystemEnum.SHRY;
  // 默认菜单开关
  systemMenuOpen: boolean,
  setting: {
    // 精度设置
    PrecisionSetting: any
  },
  // 基础配置信息
  basicConfigInfo: any;
  // 当前主题
  theme: ThemeType;
  // 全局loading 
  globalLoading: boolean;
  globalLoadingText: string;

  // authListItem
  authListItem: any;

  // 导出任务相关状态
  exportTasks: ExportTask[];
  exportTasksCount: number;

  // 下载任务相关状态
  downloadTasks: DownloadTask[];
  downloadTasksCount: number;

  // 上传任务相关状态
  uploadTasks: UploadTask[];
  uploadTasksCount: number;
}

interface GlobalAction {
  setGlobalInfo: (state: Partial<GlobalState>) => void;
  setUserInfo: (userInfo: UserInfoT) => void;
  setCollapsed: (status: boolean) => void;
  setAuthCodes: (authInfo: any) => void;
  getAuthCodes: () => any[];
  setCurrentEnterprise: (currentEnterprise: any) => void;
  setAuthorized: (authorized: boolean) => void;
  // 设置顶部菜单，2种情况，直接点击顶部菜单，企业改变。
  setCurHeaderMenuKey: (v: any) => void;
  getCurHeaderMenuKey: () => any;
  resetGlobalStore: () => void;
  getConverFactor: (a, b) => string;
  getMenuKeysMap: () => Record<string, any>;
  // 是否是默认租户
  isDefaultTenant: () => boolean,

  // 导出任务相关方法
  setExportTasks: (tasks: ExportTask[]) => void;
  setExportTasksCount: (count: number) => void;

  // 下载任务相关方法
  setDownloadTasks: (tasks: DownloadTask[]) => void;
  setDownloadTasksCount: (count: number) => void;

  // 上传任务相关方法
  setUploadTasks: (tasks: UploadTask[]) => void;
  setUploadTasksCount: (count: number) => void;
}


export type UserInfoT = Record<string, any>
const initialState: Partial<GlobalState> = {
  menuKeysMap,
  // 用户相关信息
  userInfo: null,
  // 菜单收缩
  collapsed: false,
  authCodes: [],
  currentEnterprise: null,
  authorized: false,
  enterprisesList: [],
  preRoute: '',
  productInfo: [],
  // 登录相关信息
  token: '', // 鉴权token
  tenantId: '', // 租户id，U开头代表个人
  curHeaderMenuKey: DefaultSystemKey,
  lastLoginTenantId: null,
  unitConvertMap: null,
  unitAliasMap: null,
  systemKey: SystemEnum.SYSTEM,
  systemMenuOpen: false,
  setting: {
    PrecisionSetting: {}
  },
  basicConfigInfo: {},
  theme: ThemeTypeObj.light,
  globalLoading: false,
  globalLoadingText: '',
  authListItem: null,
  exportTasks: [],
  exportTasksCount: 0,
  downloadTasks: [],
  downloadTasksCount: 0,
  uploadTasks: [],
  uploadTasksCount: 0
}

const useGlobalStore = create<Partial<GlobalState & GlobalAction>>()(persist((set, get) => ({
  ...initialState,
  resetGlobalStore: () => {
    set(initialState)
  },
  setGlobalInfo: state => set({ ...get(), ...state }),
  // 设置用户信息
  setUserInfo: (userInfo: UserInfoT) => set({ userInfo: { ...userInfo } }),
  setCollapsed: status => set({ collapsed: status }),
  setAuthCodes: (authCodes: any) => set({ authCodes: [...authCodes] }),
  getAuthCodes: () => get().authCodes,
  setCurrentEnterprise: currentEnterprise => set({ currentEnterprise }),
  setAuthorized: authorized => set({ authorized }),
  setCurHeaderMenuKey: curHeaderMenuKey => set({ curHeaderMenuKey }),
  getCurHeaderMenuKey: () => get().curHeaderMenuKey,
  getMenuKeysMap: () => menuKeysMap,
  // 是否是系统租户
  isDefaultTenant: () => {
    const { basicConfigInfo } = get()
    return basicConfigInfo?.isDefaultEpid && basicConfigInfo?.isSuperAdmin
  },
  // 获取单位换算系数
  getConverFactor(originUnit, targetUnit) {
    // 获取标准单位
    let originStandardUnit = get().unitAliasMap?.[originUnit]
    if (!originStandardUnit) return ''

    let targetStandardUnit = get().unitAliasMap?.[targetUnit]
    if (!targetStandardUnit) return ''

    // 相等返回1
    if (String(originStandardUnit).trim() === String(targetStandardUnit).trim()) return '1'
    // 获取系数
    const targetFactor = get().unitConvertMap[`${originStandardUnit}_${targetStandardUnit}`]
    return targetFactor || ''
  },
  // 设置导出任务列表
  setExportTasks: (tasks: ExportTask[]) => set({ exportTasks: tasks }),
  // 设置导出任务数量
  setExportTasksCount: (count: number) => set({ exportTasksCount: count }),

  // 设置下载任务列表
  setDownloadTasks: (tasks: DownloadTask[]) => set({ downloadTasks: tasks }),
  // 设置下载任务数量
  setDownloadTasksCount: (count: number) => set({ downloadTasksCount: count }),

  // 设置上传任务列表
  setUploadTasks: (tasks: UploadTask[]) => set({ uploadTasks: tasks }),
  // 设置上传任务数量
  setUploadTasksCount: (count: number) => set({ uploadTasksCount: count })

}), {
  name: 'global-state', // 存储的名称，默认为 localStorage
  partialize: (state) => ({ theme: state.theme }), // 仅持久化部分数据
}))

export default useGlobalStore
